import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CenteredSpinner, Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { SelectField } from '../../components/ui/SelectField';
import { ApiError } from '../../lib/api';
import { listAccounts } from '../accounts/api';
import type { Account } from '../../types/account';
import type { TaskTemplate } from '../../types/taskTemplate';
import { deleteTemplate, getTemplate, listTemplates } from './api';

type ScopeFilter = 'all' | 'global' | 'brand';

// §3.3 Task Templates section: grouped list (Global, then one group per
// brand), each row showing item count + usage count + edit/delete. Item
// counts come from a per-template fetch via useQueries — same precedent as
// PlansPage's per-plan task counts (templates are few; no denormalised
// count field to maintain on every item mutation).
export function TaskTemplatesSettings() {
  const qc = useQueryClient();
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [pendingDelete, setPendingDelete] = useState<TaskTemplate | null>(
    null,
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['taskTemplates'],
    queryFn: listTemplates,
  });
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
  });

  const templates = useMemo(
    () => templatesQuery.data ?? [],
    [templatesQuery.data],
  );

  const itemCountQueries = useQueries({
    queries: templates.map((t) => ({
      queryKey: ['taskTemplate', t.id],
      queryFn: () => getTemplate(t.id),
    })),
  });
  const itemCountById = new Map<string, number | null>();
  templates.forEach((t, i) => {
    const q = itemCountQueries[i];
    itemCountById.set(
      t.id,
      q?.isLoading ? null : (q?.data?.items.length ?? 0),
    );
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ['taskTemplates'] });
    },
    onError: (err) =>
      setDeleteError(err instanceof ApiError ? err.message : String(err)),
  });

  if (templatesQuery.isLoading || accountsQuery.isLoading) {
    return <CenteredSpinner />;
  }
  if (templatesQuery.error) {
    return (
      <p className="text-sm text-red-600">
        Failed to load templates:{' '}
        {(templatesQuery.error as Error).message}
      </p>
    );
  }

  const brands = (accountsQuery.data ?? []).filter(
    (a) => a.accountType === 'brand',
  );
  const brandById = new Map<string, Account>(brands.map((b) => [b.id, b]));

  const filtered = templates.filter((t) => {
    if (scopeFilter === 'global' && t.scope !== 'global') return false;
    if (scopeFilter === 'brand' && t.scope !== 'brand') return false;
    if (brandFilter && t.brandAccountId !== brandFilter) return false;
    return true;
  });

  const globalTemplates = filtered.filter((t) => t.scope === 'global');
  const brandGroups = new Map<string, TaskTemplate[]>();
  for (const t of filtered) {
    if (t.scope !== 'brand' || !t.brandAccountId) continue;
    const list = brandGroups.get(t.brandAccountId) ?? [];
    list.push(t);
    brandGroups.set(t.brandAccountId, list);
  }
  const orderedBrandIds = [...brandGroups.keys()].sort((a, b) =>
    (brandById.get(a)?.name ?? a).localeCompare(
      brandById.get(b)?.name ?? b,
    ),
  );

  const renderRow = (t: TaskTemplate) => {
    const count = itemCountById.get(t.id);
    return (
      <li
        key={t.id}
        className="flex items-start justify-between gap-4 border-t border-slate-100 px-4 py-3 first:border-t-0"
      >
        <div className="min-w-0">
          <Link
            to={`/settings/templates/${t.id}`}
            className="font-medium text-slate-900 hover:underline"
          >
            {t.name}
          </Link>
          {t.description && (
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {t.description}
            </p>
          )}
          <p className="mt-0.5 text-xs text-slate-500">
            {count === null
              ? '… tasks'
              : `${count} task${count === 1 ? '' : 's'}`}{' '}
            · used {t.usage} time{t.usage === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link to={`/settings/templates/${t.id}`}>
            <Button type="button" variant="outline" className="!px-2 !py-1 text-xs">
              Edit
            </Button>
          </Link>
          <Button
            type="button"
            variant="outline"
            className="!border-red-300 !px-2 !py-1 text-xs !text-red-600 hover:!bg-red-50"
            onClick={() => {
              setDeleteError(null);
              setPendingDelete(t);
            }}
          >
            Delete
          </Button>
        </div>
      </li>
    );
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SelectField
            size="sm"
            aria-label="Filter by scope"
            value={scopeFilter}
            onValueChange={(v) => setScopeFilter(v as ScopeFilter)}
            options={[
              { value: 'all', label: 'All scopes' },
              { value: 'global', label: 'Global' },
              { value: 'brand', label: 'Brand' },
            ]}
          />
          <SelectField
            size="sm"
            aria-label="Filter by brand"
            value={brandFilter}
            onValueChange={setBrandFilter}
            options={[
              { value: '', label: 'All brands' },
              ...brands.map((b) => ({ value: b.id, label: b.name })),
            ]}
          />
        </div>
        <Link to="/settings/templates/new">
          <Button type="button">+ New Template</Button>
        </Link>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          {templates.length === 0
            ? 'No task templates yet. Click "+ New Template" to create one.'
            : 'No templates match the current filters.'}
        </div>
      ) : (
        <div className="space-y-6">
          {globalTemplates.length > 0 && (
            <section>
              <h3 className="mb-1 text-sm font-semibold text-slate-700">
                Global ({globalTemplates.length})
              </h3>
              <ul className="overflow-hidden rounded-md border border-slate-200 bg-white">
                {globalTemplates.map(renderRow)}
              </ul>
            </section>
          )}
          {orderedBrandIds.map((bid) => {
            const list = brandGroups.get(bid) ?? [];
            return (
              <section key={bid}>
                <h3 className="mb-1 text-sm font-semibold text-slate-700">
                  Brand: {brandById.get(bid)?.name ?? bid} ({list.length})
                </h3>
                <ul className="overflow-hidden rounded-md border border-slate-200 bg-white">
                  {list.map(renderRow)}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      {pendingDelete && (
        <DeleteTemplateModal
          template={pendingDelete}
          busy={deleteMut.isPending}
          error={deleteError}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => deleteMut.mutate(pendingDelete.id)}
        />
      )}
    </div>
  );
}

// §3.7 (decision: simple confirmation modal — type-DELETE is reserved for
// Plan/Rollout per the Deletion spec; a template delete only cascades its
// own items and never breaks existing Plans, since applied tasks snapshot
// from the template at apply time, §4.5).
function DeleteTemplateModal({
  template,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  template: TaskTemplate;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="text-sm font-semibold text-slate-900">
          Delete template
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          Delete{' '}
          <span className="font-medium">{template.name}</span> and all its
          task items? This can't be undone.
        </p>
        {template.usage > 0 && (
          <p className="mt-2 text-xs text-slate-500">
            Used {template.usage} time{template.usage === 1 ? '' : 's'}. Tasks
            already applied to Plans are independent snapshots and won't be
            affected — only this template won't be usable for new Plans.
          </p>
        )}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            variant="destructive"
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Spinner /> Deleting…
              </span>
            ) : (
              'Delete'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
